import { PERIODS, type Period, type PeriodKey } from './resolution'

/**
 * "Change interval" quick-switch parsing.
 *
 * While a chart is focused, typing a number (optionally followed by a unit
 * letter) builds a short buffer like `"5"`, `"15"`, `"1h"`, `"4h"`, `"1d"`.
 * Enter applies it. These pure helpers turn that raw buffer into a
 * human-readable label and, when the result maps onto one of our fixed
 * `PERIODS`, the matching `PeriodKey` to apply.
 *
 * Units (a bare number means minutes):
 *   (none)   → minute
 *   h / H    → hour
 *   d / D    → day
 *   w / W    → week
 *   M        → month   (capital only; lowercase 'm' is never typed for minutes)
 */

const UNIT_TIMESPAN: Record<string, Period['timespan']> = {
  '': 'minute',
  h: 'hour',
  d: 'day',
  w: 'week',
  M: 'month',
  // No 'y': the yearly period was removed (monthly is the top of the ladder),
  // so a "1y" buffer would resolve against PERIODS, find nothing, and silently
  // no-op while the help text still advertised it.
}

const TIMESPAN_WORD: Record<Period['timespan'], string> = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year', // unreachable via input; kept to satisfy the timespan union
}

/** True for a single char that can be appended to the interval buffer. */
export function isIntervalChar(char: string, buffer: string): boolean {
  if (/^[0-9]$/.test(char)) {
    // No leading zero (a bare "0" is meaningless).
    return !(buffer === '' && char === '0')
  }
  // A unit letter is only valid right after one or more digits, and only once.
  if (/^[hHdDwWM]$/.test(char)) return /[0-9]$/.test(buffer)
  return false
}

interface ParsedInterval {
  multiplier: number
  timespan: Period['timespan']
}

function parseBuffer(buffer: string): ParsedInterval | null {
  const m = /^([0-9]+)([hHdDwWMyY]?)$/.exec(buffer)
  if (!m) return null
  const multiplier = parseInt(m[1], 10)
  if (multiplier <= 0) return null
  // Normalise the unit letter to its canonical key (H→h, D→d, W→w, Y→y; M stays M).
  const raw = m[2]
  const unit = raw === '' ? '' : raw === 'M' ? 'M' : raw.toLowerCase()
  const timespan = UNIT_TIMESPAN[unit]
  if (!timespan) return null
  return { multiplier, timespan }
}

function describe({ multiplier, timespan }: ParsedInterval): string {
  const word = TIMESPAN_WORD[timespan]
  return `${multiplier} ${word}${multiplier > 1 ? 's' : ''}`
}

function findKey(multiplier: number, timespan: Period['timespan']): PeriodKey | null {
  const entry = (Object.entries(PERIODS) as [PeriodKey, Period][]).find(
    ([, p]) => p.multiplier === multiplier && p.timespan === timespan,
  )
  return entry?.[0] ?? null
}

function resolveKey({ multiplier, timespan }: ParsedInterval): PeriodKey | null {
  const direct = findKey(multiplier, timespan)
  if (direct) return direct
  // A bare minute count that is a whole number of hours applies the hourly
  // period: "60" is 1H and "240" is 4H (Chris, 2026-09-22), the way other
  // platforms accept minute counts for hourly charts. The label still reads
  // in minutes, since the HUD echoes the typed digits and only appends the
  // unit word.
  if (timespan === 'minute' && multiplier % 60 === 0) {
    return findKey(multiplier / 60, 'hour')
  }
  return null
}

export interface IntervalInterpretation {
  /** Human-readable description, e.g. "1 minute", "5 minutes", "4 hours". */
  label: string
  /** The matching fixed period, or null when no supported interval matches. */
  periodKey: PeriodKey | null
  /** Whether `periodKey` is non-null (i.e. Enter would apply something). */
  valid: boolean
}

/**
 * Interpret a raw interval buffer. Returns null when the buffer can't be parsed
 * at all (empty or malformed); otherwise a label + the resolved period (which
 * may be null when the typed interval isn't one we support).
 */
export function interpretInterval(buffer: string): IntervalInterpretation | null {
  const parsed = parseBuffer(buffer)
  if (!parsed) return null
  const periodKey = resolveKey(parsed)
  return { label: describe(parsed), periodKey, valid: periodKey !== null }
}
